import { useEffect, useRef, useState } from 'react';
import {
  requestOtp,
  verifyOtp,
  type OnboardingState,
  type OtpChallenge,
} from '../../api/endpoints.js';
import { useAction } from '../../api/hooks.js';
import { Failure } from '../../ui/kit.js';
import { FlowScreen, FlowSub, FlowTitle, PrimaryButton } from '../../ui/flow.js';

/**
 * Mobile screens 1–2: phone + T&C acknowledgment, then the six-box code that
 * verifies itself. The OTP screen is the flow's router — here the routing
 * decision comes from the verify response's own `onboarding` block, which is
 * this backend's replacement for the legacy /check-user oracle.
 */

const CALLING_CODES = ['+1', '+91', '+44', '+61', '+81', '+49', '+33', '+971'] as const;

/** People type "+91 85558 14854". Same number, and a 400 without this. */
function digitsOnly(raw: string): string {
  return raw.replace(/\D/g, '');
}

const CODE_LENGTH = 6;

function secondsUntil(iso: string): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 1000));
}

/**
 * PHONE_COOLDOWN's retry_after spans four orders of magnitude — minutes on
 * dev, 7 days in production — so "in 604800s" is not a sentence. The two
 * largest units, remainder rounded UP (never promise a shorter wait than the
 * real one), with the carry handled so "23 hours 60 minutes" becomes "1 day".
 */
export function formatWait(totalSeconds: number): string {
  const units: [label: string, seconds: number][] = [
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
    ['second', 1],
  ];
  const total = Math.max(1, totalSeconds);
  const index = Math.max(
    0,
    units.findIndex(([, size]) => total >= size),
  );
  const smaller = units[Math.min(index + 1, units.length - 1)]!;
  // Round up in the SMALLER unit first; decomposition then never overshoots.
  const rounded = Math.ceil(total / smaller[1]) * smaller[1];
  // Rounding can carry across the unit boundary (23h59m → 24 hours): re-enter
  // with the rounded value, which is its own fixed point, so this recurses once.
  if (index > 0 && rounded >= units[index - 1]![1]) return formatWait(rounded);
  const [label, size] = units[index]!;
  const primary = Math.floor(rounded / size);
  const secondary = index === units.length - 1 ? 0 : (rounded - primary * size) / smaller[1];
  const phrase = (count: number, unit: string) => `${String(count)} ${unit}${count === 1 ? '' : 's'}`;
  if (primary === 0) return phrase(secondary, smaller[0]);
  if (secondary === 0) return phrase(primary, label);
  return `${phrase(primary, label)} ${phrase(secondary, smaller[0])}`;
}

export function AuthScreen({ onVerified }: { onVerified: (onboarding: OnboardingState) => void }) {
  const [callingCode, setCallingCode] = useState<string>('+1');
  const [digits, setDigits] = useState('');
  const [termsChecked, setTermsChecked] = useState(false);
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);

  const request = useAction(requestOtp, setChallenge);

  const phone = `${callingCode}${digits}`;

  if (challenge !== null) {
    return (
      <OtpStage
        phone={phone}
        challenge={challenge}
        onBack={() => {
          setChallenge(null);
          request.reset();
        }}
        onNewChallenge={setChallenge}
        onVerified={onVerified}
      />
    );
  }

  const continueDisabled = digits.trim().length === 0 || !termsChecked || request.pending;

  return (
    <FlowScreen
      progress={15}
      footer={
        <PrimaryButton onClick={() => request.run(phone)} disabled={continueDisabled}>
          {request.pending ? 'Sending…' : 'Continue'}
        </PrimaryButton>
      }
    >
      <FlowTitle>Enter your phone number</FlowTitle>
      <FlowSub>We&rsquo;ll send you a verification code to confirm your identity.</FlowSub>

      <span className="flow-field-label">Phone Number</span>
      <div className="phone-card">
        <select
          className="phone-code"
          value={callingCode}
          onChange={(event) => setCallingCode(event.target.value)}
          disabled={request.pending}
          aria-label="Country calling code"
        >
          {CALLING_CODES.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
        <span className="phone-divider" aria-hidden="true" />
        <input
          className="phone-digits"
          type="tel"
          inputMode="numeric"
          placeholder="555 123 4567"
          value={digits}
          disabled={request.pending}
          onChange={(event) => {
            // A pasted "+<code>…" keeps its digits, minus the selected code.
            const raw = event.target.value;
            let cleaned = digitsOnly(raw);
            const codeDigits = digitsOnly(callingCode);
            if (raw.trim().startsWith('+') && cleaned.startsWith(codeDigits)) {
              cleaned = cleaned.slice(codeDigits.length);
            }
            setDigits(cleaned);
          }}
        />
      </div>
      <p className="flow-note">Message and data rates may apply. Privacy first.</p>

      <label className="consent-row">
        <input
          type="checkbox"
          checked={termsChecked}
          onChange={(event) => setTermsChecked(event.target.checked)}
        />
        <span>
          By checking this box, I confirm that I have read and agree to the{' '}
          <a href="https://imageshield.com/terms-and-conditions" target="_blank" rel="noreferrer noopener">
            Terms and Conditions
          </a>{' '}
          of the use of ImageShield, including the{' '}
          <a
            href="https://imageshield.com/terms-and-conditions#biometrics"
            target="_blank"
            rel="noreferrer noopener"
          >
            Biometric Data Collection and Consent
          </a>{' '}
          section. I understand and consent to ImageShield collecting and processing my biometric
          information.
        </span>
      </label>

      {request.error !== null && (
        <Failure
          error={request.error}
          hint={
            request.error.code === 'SMS_DELIVERY_FAILED'
              ? 'The api refuses to claim a code is on the way when the SMS provider failed — on dev, a Twilio trial account only delivers to numbers verified in its console.'
              : undefined
          }
        />
      )}
    </FlowScreen>
  );
}

function OtpStage({
  phone,
  challenge,
  onBack,
  onNewChallenge,
  onVerified,
}: {
  phone: string;
  challenge: OtpChallenge;
  onBack: () => void;
  onNewChallenge: (challenge: OtpChallenge) => void;
  onVerified: (onboarding: OnboardingState) => void;
}) {
  const [boxes, setBoxes] = useState<string[]>(Array<string>(CODE_LENGTH).fill(''));
  const [cooldown, setCooldown] = useState(() => secondsUntil(challenge.resend_after));
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const verify = useAction(verifyOtp, (result) => onVerified(result.onboarding));
  const resend = useAction(requestOtp, (fresh) => {
    onNewChallenge(fresh);
    setBoxes(Array<string>(CODE_LENGTH).fill(''));
    setCooldown(secondsUntil(fresh.resend_after));
    inputs.current[0]?.focus();
  });

  useEffect(() => {
    const timer = window.setInterval(
      () => setCooldown(secondsUntil(challenge.resend_after)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [challenge.resend_after]);

  // Verification fires the moment all six digits are in — a tap on a fully
  // entered code was needless friction (the mobile screen's own QC change).
  const code = boxes.join('');
  useEffect(() => {
    if (code.length === CODE_LENGTH && !verify.pending) {
      verify.run(challenge.challenge_id, code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const fillFrom = (start: number, text: string) => {
    const incoming = digitsOnly(text);
    if (incoming.length === 0) {
      const next = [...boxes];
      next[start] = '';
      setBoxes(next);
      return;
    }
    // Paste / SMS autofill: anything longer than one digit fills from box 0.
    const from = incoming.length > 1 ? 0 : start;
    const next = incoming.length > 1 ? Array<string>(CODE_LENGTH).fill('') : [...boxes];
    for (let index = 0; index < incoming.length && from + index < CODE_LENGTH; index += 1) {
      next[from + index] = incoming[index] ?? '';
    }
    setBoxes(next);
    verify.reset();
    const focusAt = Math.min(from + incoming.length, CODE_LENGTH - 1);
    inputs.current[focusAt]?.focus();
  };

  return (
    <FlowScreen progress={30} onBack={onBack}>
      <FlowTitle>Enter verification code</FlowTitle>
      <FlowSub>
        We sent a 6-digit code to {phone}. Code expires in 5&nbsp;minutes.
      </FlowSub>

      <div className="otp-row">
        {boxes.map((digit, index) => (
          <input
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            ref={(element) => {
              inputs.current[index] = element;
            }}
            className={digit.length > 0 ? 'otp-box filled' : 'otp-box'}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={CODE_LENGTH}
            value={digit}
            disabled={verify.pending}
            onChange={(event) => fillFrom(index, event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Backspace' && digit.length === 0 && index > 0) {
                const next = [...boxes];
                next[index - 1] = '';
                setBoxes(next);
                inputs.current[index - 1]?.focus();
              }
            }}
          />
        ))}
      </div>

      {verify.error !== null && (
        <p className="flow-error">
          {verify.error.code === 'PHONE_COOLDOWN'
            ? `This number belonged to a recently deleted account. It can open a new one in ${formatWait(verify.error.retryAfter ?? 0)}.`
            : ({
                INVALID_CODE: 'Invalid code. Please try again.',
                CHALLENGE_EXPIRED: 'This code expired — request a fresh one below.',
                TOO_MANY_ATTEMPTS: 'Too many attempts — this code is burnt. Request a fresh one.',
                ACCOUNT_DELETING:
                  'This account is being deleted. Once deletion finishes and its waiting period passes, the number can start a new account.',
              }[verify.error.code] ?? verify.error.message)}
        </p>
      )}
      {verify.pending && <p className="flow-verifying">Verifying…</p>}

      <button
        type="button"
        className="flow-link"
        disabled={cooldown > 0 || resend.pending}
        onClick={() => resend.run(phone)}
      >
        {resend.pending
          ? 'Sending…'
          : cooldown > 0
            ? `Resend code in ${String(cooldown)}s`
            : 'Resend code'}
      </button>
      {resend.error !== null && <Failure error={resend.error} />}
    </FlowScreen>
  );
}
